import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiContext } from '@api/api';
import { AsyncJobDetailOutDTO, AsyncJobItemOutDTO, JobStatusEnum, JobTypeEnum, PermissionTypesEnum } from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgReset from '@icons/reset-filled.svg?react';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { useJobChannel } from '@hooks/useJobs/useJobChannel.ts';
import { formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';

interface ComponentProps {
  jobId: string;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];

const LadenFactorJobDetail: React.FC<ComponentProps> = ({ jobId }) => {
  const { getAsyncJobDetail, archiveJob, retryJob } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const [detail, setDetail] = useState<AsyncJobDetailOutDTO>();
  const [action, setAction] = useState<string | null>(null);

  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const load = (onLoaded?: () => void) =>
    getAsyncJobDetail(jobId, d => {
      setDetail(d);
      onLoaded?.();
    });

  useEffect(() => {
    load();
  }, [jobId]);

  const { live } = useJobChannel(jobId, {
    onProgress: e => {
      setDetail(prev =>
        prev == null
          ? prev
          : {
              ...prev,
              job: {
                ...prev.job,
                jobStatus: (e.status as JobStatusEnum) ?? prev.job.jobStatus,
                processedCount: e.processed ?? prev.job.processedCount,
                successCount: e.success ?? prev.job.successCount,
                failedCount: e.failed ?? prev.job.failedCount,
                totalCount: e.total ?? prev.job.totalCount,
              },
            },
      );
    },
    onFinished: () => load(),
  });

  const running = detail != null && !TERMINAL.includes(detail.job.jobStatus);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => load(), live ? 60000 : 15000);
    return () => clearInterval(id);
  }, [jobId, running, live]);

  if (detail == null) return <SvgLoader className={'animate-spin'} />;

  const { job } = detail;
  const isTerminal = TERMINAL.includes(job.jobStatus);
  const title = `${tr('JobDetail.task', 'Úkol')} ${job.applicationId ?? ''}`;

  const canRetry =
    job.jobType === JobTypeEnum.LADEN_FACTOR_PDF_ORDER && (job.jobStatus === JobStatusEnum.ERROR || (isTerminal && job.failedCount > 0)) && jobsRwFlag.granted;

  const retry = () => {
    setAction('retry');
    retryJob(jobId, () => load(() => setAction(null)), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  const itemError = (item: AsyncJobItemOutDTO) => {
    let error;
    try {
      error = item.resultJson ? JSON.parse(item.resultJson)?.error : undefined;
    } catch {
      error = undefined;
    }
    return formatJobItemError(error, item.errorMessage, tr);
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${job.jobType}`, `${job.jobType}`)}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={job.createdAt}
        buttons={
          canRetry ? (
            <RingilButton
              mode={'outlined-rectangle'}
              icon={<SvgReset />}
              label={tr('JobList.retry', 'Zkusit znovu')}
              dataTestId={'laden-factor-job-retry'}
              onClick={retry}
              loading={action === 'retry'}
              disabled={action != null && action !== 'retry'}
            />
          ) : undefined
        }
        onArchive={isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      <div className={'flex flex-row items-center gap-4 rounded bg-white p-4'}>
        {!isTerminal && <SvgLoader className={'animate-spin'} />}
        <span>
          {job.jobStatus === JobStatusEnum.DONE && job.failedCount > 0
            ? tr('JobList.state.DONE_WITH_ERRORS', 'Hotovo s chybami ({{count}})', { count: job.failedCount })
            : tr(`JobList.status.${job.jobStatus}`, `${job.jobStatus}`)}
        </span>
        <span className={'text-content-secondary'}>
          {tr('JobDetail.ladenFactor.counts', '{{success}} OK, {{failed}} failed, {{total}} total', {
            success: job.successCount,
            failed: job.failedCount,
            total: job.totalCount ?? detail.items.length,
          })}
        </span>
      </div>

      <div className={'flex flex-col rounded bg-white'}>
        {detail.items.map(item => (
          <div key={item.itemId} className={'flex flex-row items-center gap-4 border-b border-gray-200 p-3 last:border-b-0'}>
            {item.itemKey != null && (
              <Link to={nav.createNavigationLink(nav.urlFunctions.createExpeditionDetailV2(item.itemKey))}>
                {tr('JobDetail.ladenFactor.expedition', 'Expedition')}
              </Link>
            )}
            <span>{tr(`JobDetail.itemStatus.${item.itemStatus}`, `${item.itemStatus}`)}</span>
            <span className={'text-content-tertiary text-sm'}>{itemError(item)}</span>
          </div>
        ))}
      </div>

      <JobExpiryNote expiresAt={detail.expiresAt} />
    </div>
  );
};

export default LadenFactorJobDetail;
